import { useState } from 'react';
import { Option } from '@trukt/types/Option';
import { formatDate } from '@trukt/portal-utils/formatDate';
import { CompoundFilterStateOption, RenderOptionProps } from '@trukt/ui/CompoundFilter';
import DateInput from '@trukt/ui/DateInput';
import { SelectOption } from '@trukt/ui/SelectOption';
import { MOCKED_DATE_OPTIONS } from '@components/pages/OrderListView/hooks/useCompoundFilterItems/useCompoundFilterItems.constants';
import { DateLiterals } from '@api';
import { DateTime } from 'luxon';

export type DateFilterData = {
  dateType: string;
  dateStart: string;
  dateEnd: string;
  dateTypeValue: DateLiterals;
  mode: 'same-date' | 'range';
};
type DateFilterOption = Option<DateLiterals>;
type DateInputType = [Date | null, Date | null];

export const DateFilter = ({ addOption }: RenderOptionProps) => {
  const [select, setSelect] = useState<DateFilterOption | null>(null);
  const [dates, setDates] = useState<DateInputType>([null, null]);

  const handleDateChange = (datesRange: DateInputType) => {
    const [startDate, endDate] = datesRange;

    if (!startDate || !select) return;

    if (
      dates[0] !== null &&
      DateTime.fromJSDate(dates[0]).equals(DateTime.fromJSDate(startDate)) &&
      dates[1] === null &&
      endDate === null
    ) {
      setDates([startDate, startDate]);

      addOption<DateFilterData>(
        buildDateOption({
          dateType: select.label,
          dateStart: startDate.toISOString(),
          dateEnd: startDate.toISOString(),
          dateTypeValue: select.value,
          mode: 'same-date',
        }),
      );
    } else {
      setDates(datesRange);

      if (!endDate) return;

      addOption<DateFilterData>(
        buildDateOption({
          dateType: select.label,
          dateStart: startDate.toISOString(),
          dateEnd: endDate.toISOString(),
          dateTypeValue: select.value,
          mode: 'range',
        }),
      );
    }

    setDates([null, null]);
    setSelect(null);
  };

  return (
    <section className="flex items-center gap-2">
      <SelectOption
        aria-label="Date option"
        options={MOCKED_DATE_OPTIONS}
        classNames={{ root: 'w-48' }}
        onChange={setSelect}
        value={select}
        placeholder="Type"
      />
      <DateInput
        iconOnly
        disabled={!select}
        startDate={dates[0]}
        endDate={dates[1]}
        selectsRange
        onChange={handleDateChange}
        aria-label="Date input"
      />
    </section>
  );
};

export function buildDateOption(
  data: DateFilterData,
): CompoundFilterStateOption<DateFilterData, string> {
  const label =
    data.mode === 'same-date'
      ? `${data.dateType} (${formatDate(data.dateStart)})`
      : `${data.dateType} (${formatDate(data.dateStart)} - ${formatDate(data.dateEnd)})`;
  return {
    id: data.dateType + data.dateStart + data.dateEnd,
    label,
    stateModifiers: {
      isActive: true,
    },
    data,
  };
}
